import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["input", "dropdown", "option", "hiddenInput"]
  static values = { selected: String }

  connect() {
    this.originalOptions = this.optionTargets.map(option => ({
      element: option,
      text: option.textContent.trim(),
      value: option.dataset.value,
      group: option.dataset.group
    }))
    
    // Initialize keyboard navigation
    this.highlightedIndex = -1
    
    // Set initial value if provided
    if (this.selectedValue) {
      this.inputTarget.value = this.getOptionText(this.selectedValue)
      this.hiddenInputTarget.value = this.selectedValue
    }
    
    // Store the original placeholder
    this.originalPlaceholder = this.inputTarget.placeholder
    
    // Close dropdown when clicking outside
    document.addEventListener('click', this.closeOnOutsideClick.bind(this), true)
  }

  disconnect() {
    document.removeEventListener('click', this.closeOnOutsideClick.bind(this), true)
  }

  closeOnOutsideClick(event) {
    // Check if dropdown is open first
    if (this.dropdownTarget.classList.contains('hidden')) {
      return
    }
    
    // Check if click is outside the timezone search component
    if (!this.element.contains(event.target)) {
      // Restore original value when clicking outside without selection
      if (this.previousValue !== undefined) {
        this.inputTarget.value = this.previousValue
      }
      this.inputTarget.placeholder = this.originalPlaceholder
      this.hideDropdown()
    }
  }

  search() {
    const query = this.inputTarget.value.toLowerCase()
    
    if (query.length === 0) {
      this.showAllOptions()
    } else {
      this.filterOptions(query)
    }
    
    // Reset highlight when search changes
    this.highlightedIndex = -1
    this.updateHighlight()
    
    this.showDropdown()
  }

  showAllOptions() {
    this.optionTargets.forEach(option => {
      option.style.display = 'block'
    })
    
    // Show all group headers
    const groupHeaders = this.dropdownTarget.querySelectorAll('[data-group-header]')
    groupHeaders.forEach(header => {
      header.style.display = 'block'
    })
  }

  filterOptions(query) {
    const groups = {}
    
    this.originalOptions.forEach(option => {
      const matches = option.text.toLowerCase().includes(query)
      option.element.style.display = matches ? 'block' : 'none'
      
      if (matches) {
        if (!groups[option.group]) {
          groups[option.group] = true
        }
      }
    })
    
    // Show/hide group headers based on whether they have visible options
    const groupHeaders = this.dropdownTarget.querySelectorAll('[data-group-header]')
    groupHeaders.forEach(header => {
      const groupName = header.dataset.groupHeader
      header.style.display = groups[groupName] ? 'block' : 'none'
    })
  }

  selectOption(event) {
    const value = event.currentTarget.dataset.value
    const text = event.currentTarget.textContent.trim()
    
    this.inputTarget.value = text
    this.hiddenInputTarget.value = value
    this.hideDropdown()
    
    // Form will be submitted when user clicks Done button
  }

  selectHighlightedOption() {
    const visibleOptions = this.getVisibleOptions()
    if (this.highlightedIndex >= 0 && this.highlightedIndex < visibleOptions.length) {
      const option = visibleOptions[this.highlightedIndex]
      const value = option.dataset.value
      const text = option.textContent.trim()
      
      this.inputTarget.value = text
      this.hiddenInputTarget.value = value
      this.hideDropdown()
      
      // Form will be submitted when user clicks Done button
    }
  }

  getVisibleOptions() {
    return this.optionTargets.filter(option => option.style.display !== 'none')
  }

  updateHighlight() {
    const visibleOptions = this.getVisibleOptions()
    
    // Remove previous highlights
    this.optionTargets.forEach(option => {
      option.classList.remove('bg-blue-50', 'text-blue-600')
    })
    
    // Add highlight to current option
    if (this.highlightedIndex >= 0 && this.highlightedIndex < visibleOptions.length) {
      const option = visibleOptions[this.highlightedIndex]
      option.classList.add('bg-blue-50', 'text-blue-600')
      
      // Scroll into view if needed
      option.scrollIntoView({ block: 'nearest' })
    }
  }

  navigateUp() {
    const visibleOptions = this.getVisibleOptions()
    if (visibleOptions.length === 0) return
    
    this.highlightedIndex = this.highlightedIndex <= 0 
      ? visibleOptions.length - 1 
      : this.highlightedIndex - 1
    
    this.updateHighlight()
  }

  navigateDown() {
    const visibleOptions = this.getVisibleOptions()
    if (visibleOptions.length === 0) return
    
    this.highlightedIndex = this.highlightedIndex >= visibleOptions.length - 1 
      ? 0 
      : this.highlightedIndex + 1
    
    this.updateHighlight()
  }

  showDropdown() {
    this.dropdownTarget.classList.remove('hidden')
  }

  hideDropdown() {
    this.dropdownTarget.classList.add('hidden')
    
    // Reset highlight
    this.highlightedIndex = -1
    this.updateHighlight()
    
    // If no selection was made and input is empty, restore the original value
    if (this.inputTarget.value.trim() === '' && this.hiddenInputTarget.value) {
      this.inputTarget.value = this.getOptionText(this.hiddenInputTarget.value)
      this.inputTarget.placeholder = this.originalPlaceholder
    }
  }

  toggleDropdown() {
    if (this.dropdownTarget.classList.contains('hidden')) {
      // Store current value before clearing
      this.previousValue = this.inputTarget.value
      
      // Clear the input and show search placeholder
      this.inputTarget.value = ''
      this.inputTarget.placeholder = 'Type to search timezones...'
      
      this.showAllOptions()
      this.showDropdown()
      this.inputTarget.focus()
    } else {
      this.hideDropdown()
    }
  }

  getOptionText(value) {
    const option = this.originalOptions.find(opt => opt.value === value)
    return option ? option.text : value
  }

  handleKeydown(event) {
    if (event.key === 'Escape') {
      // Restore previous value if escaping without selection
      if (this.previousValue && this.inputTarget.value.trim() === '') {
        this.inputTarget.value = this.previousValue
      }
      this.inputTarget.placeholder = this.originalPlaceholder
      this.hideDropdown()
    } else if (event.key === 'ArrowDown') {
      event.preventDefault()
      if (this.dropdownTarget.classList.contains('hidden')) {
        this.toggleDropdown()
      } else {
        this.navigateDown()
      }
    } else if (event.key === 'ArrowUp') {
      event.preventDefault()
      if (!this.dropdownTarget.classList.contains('hidden')) {
        this.navigateUp()
      }
    } else if (event.key === 'Enter') {
      event.preventDefault()
      if (!this.dropdownTarget.classList.contains('hidden')) {
        this.selectHighlightedOption()
      }
    }
  }
}